import type {
  ClaimFieldValidation,
  ClaimFormField,
  ClaimValidationRule,
} from "../../types";

function validateFiles(
  field: ClaimFormField,
  files: File[]
): string | undefined {
  const allowed = field.fileUpload?.allowedFileTypes?.map((t) =>
    t.toLowerCase().replace(/^\./, "")
  );
  const maxMB = field.fileUpload?.maxFileSizeMB;
  const fallback = field.validation?.errorMessage;

  for (const file of files) {
    if (allowed?.length) {
      const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
      if (!allowed.includes(ext)) {
        return (
          fallback ||
          `Only ${allowed.join(", ").toUpperCase()} files are allowed.`
        );
      }
    }
    if (maxMB && file.size > maxMB * 1024 * 1024) {
      return fallback || `Each file must be under ${maxMB}MB.`;
    }
  }
  return undefined;
}

function validateDateWindow(
  validation: ClaimFieldValidation | undefined,
  value: string
): string | undefined {
  const min = validation?.minDate;
  const max = validation?.maxDate;
  if (min?.value && value < min.value) return min.message;
  if (max?.value && value > max.value) return max.message;
  return undefined;
}

function validateRules(
  rules: ClaimValidationRule[] | undefined,
  value: string
): string | undefined {
  if (!rules?.length) return undefined;
  const num = Number(value);
  for (const rule of rules) {
    const target = Number(rule.value);
    if (Number.isNaN(target)) continue;
    if (rule.type === "min" && (Number.isNaN(num) || num < target)) {
      return rule.errorMessage;
    }
    if (rule.type === "max" && (Number.isNaN(num) || num > target)) {
      return rule.errorMessage;
    }
  }
  return undefined;
}

export function validateField(
  field: ClaimFormField,
  value: string,
  files: File[]
): string | undefined {
  const validation = field.validation;
  const empty =
    field.type === "fileUploader" ? files.length === 0 : !value.trim();

  if (field.isMandatory && empty) {
    return validation?.errorMessage;
  }
  if (empty) return undefined;

  if (field.type === "fileUploader") return validateFiles(field, files);

  if (field.type === "datePicker") {
    const dateError = validateDateWindow(validation, value);
    if (dateError) return dateError;
  }

  if (validation?.regex) {
    try {
      if (!new RegExp(`^(?:${validation.regex})$`).test(value)) {
        return validation.errorMessage;
      }
    } catch {
      /* invalid config regex */
    }
  }

  return validateRules(validation?.rules, value);
}

export function validateForm(
  fields: ClaimFormField[],
  values: Record<string, string>,
  files: Record<string, File[]>,
  keyOf: (field: ClaimFormField, index: number) => string
): Record<string, string> {
  const errors: Record<string, string> = {};
  fields.forEach((field, index) => {
    const key = keyOf(field, index);
    const error = validateField(field, values[key] ?? "", files[key] ?? []);
    if (error) errors[key] = error;
  });
  return errors;
}
